import { userRoles, type PortalRole } from '../../roles';

export const DASHBOARD_WIDGET_CATALOG = [
  'summary',
  'trend',
  'statuses',
  'services',
  'recent',
  'awaiting',
  'unread_contacts',
  'missed_calls',
  'planned',
  'activity',
  'notices',
  'sla',
  'workload',
  'worklogs',
  'system_health',
  'server',
  'telephony',
  'integrations',
] as const;

export type DashboardWidgetKey = typeof DASHBOARD_WIDGET_CATALOG[number];
export type DashboardWidgetPreference = { key: DashboardWidgetKey; enabled: boolean };

function widgets(...keys: DashboardWidgetKey[]): readonly DashboardWidgetKey[] {
  return Object.freeze(keys);
}

const ROLE_WIDGET_ACCESS: Readonly<Record<string, readonly DashboardWidgetKey[]>> = Object.freeze({
  EXECUTOR: widgets('summary', 'recent', 'awaiting', 'unread_contacts', 'missed_calls', 'planned'),
  DISPATCHER: widgets('summary', 'statuses', 'services', 'unread_contacts', 'missed_calls', 'sla', 'workload', 'notices'),
  MANAGER: widgets('summary', 'trend', 'statuses', 'services', 'sla', 'workload', 'worklogs', 'activity', 'notices'),
  ADMIN: widgets('summary', 'services', 'recent', 'awaiting', 'planned', 'notices', 'system_health', 'server', 'telephony', 'integrations'),
  OBSERVER: widgets('summary', 'trend', 'statuses', 'services', 'sla', 'activity', 'notices'),
  CLIENT: widgets('summary', 'trend', 'statuses', 'recent', 'awaiting', 'notices'),
});

const ROLE_WIDGET_LAYOUTS = Object.freeze({
  ADMIN: widgets(
    'system_health', 'server', 'notices', 'summary', 'services', 'telephony', 'integrations', 'planned',
    'recent', 'awaiting', 'trend', 'sla', 'workload', 'unread_contacts',
    'missed_calls', 'worklogs', 'statuses', 'activity',
  ),
  MANAGER: widgets(
    'summary', 'notices', 'trend', 'sla', 'workload', 'services', 'unread_contacts',
    'missed_calls', 'worklogs', 'planned', 'statuses', 'activity', 'recent', 'awaiting',
    'system_health', 'telephony', 'integrations',
  ),
  SPECIALIST: widgets(
    'awaiting', 'summary', 'notices', 'sla', 'recent', 'workload', 'services',
    'unread_contacts', 'missed_calls', 'planned', 'worklogs', 'statuses', 'activity',
    'trend', 'system_health', 'telephony', 'integrations',
  ),
  CLIENT: widgets('summary', 'recent', 'awaiting', 'trend', 'statuses', 'notices'),
});

const DASHBOARD_LAYOUT_VERSION = 4;

function dashboardRoles(
  userType: 'EMPLOYEE' | 'CLIENT',
  rolesOrRole: unknown,
  legacyRole?: unknown,
): PortalRole[] {
  if (userType === 'CLIENT') return ['CLIENT'];
  return userRoles({
    userType,
    role: String(Array.isArray(rolesOrRole) ? legacyRole ?? '' : rolesOrRole ?? ''),
    roles: Array.isArray(rolesOrRole) ? rolesOrRole.map(String) : undefined,
  });
}

export function dashboardWidgetCatalog(
  userType: 'EMPLOYEE' | 'CLIENT',
  rolesOrRole: unknown,
  legacyRole?: unknown,
): DashboardWidgetKey[] {
  const roles = dashboardRoles(userType, rolesOrRole, legacyRole);
  const allowed = new Set<DashboardWidgetKey>();
  for (const role of roles) {
    for (const key of ROLE_WIDGET_ACCESS[role] ?? ROLE_WIDGET_ACCESS.EXECUTOR) allowed.add(key);
  }
  const layout = userType === 'CLIENT'
    ? ROLE_WIDGET_LAYOUTS.CLIENT
    : roles.includes('ADMIN')
      ? ROLE_WIDGET_LAYOUTS.ADMIN
      : roles.includes('MANAGER')
        ? ROLE_WIDGET_LAYOUTS.MANAGER
        : ROLE_WIDGET_LAYOUTS.SPECIALIST;
  const ordered = layout.filter(key => allowed.has(key));
  for (const key of DASHBOARD_WIDGET_CATALOG) {
    if (allowed.has(key) && !ordered.includes(key)) ordered.push(key);
  }
  return ordered;
}

export function dashboardDefaults(
  userType: 'EMPLOYEE' | 'CLIENT',
  rolesOrRole: unknown,
  legacyRole?: unknown,
): DashboardWidgetKey[] {
  return dashboardWidgetCatalog(userType, rolesOrRole, legacyRole);
}

function preferenceEntries(saved: unknown): Array<[string, boolean]> {
  if (typeof saved === 'string') {
    try { return preferenceEntries(JSON.parse(saved)); } catch { return []; }
  }
  if (Array.isArray(saved)) {
    return saved.flatMap(item => {
      if (!item || typeof item !== 'object') return [];
      const candidate = item as { key?: unknown; enabled?: unknown };
      return typeof candidate.key === 'string' && typeof candidate.enabled === 'boolean'
        ? [[candidate.key, candidate.enabled] as [string, boolean]]
        : [];
    });
  }
  if (saved && typeof saved === 'object') {
    return Object.entries(saved).flatMap(([key, enabled]) => (
      typeof enabled === 'boolean' ? [[key, enabled] as [string, boolean]] : []
    ));
  }
  return [];
}

export function normalizeDashboardPreferences(
  catalog: readonly DashboardWidgetKey[],
  defaults: readonly DashboardWidgetKey[],
  saved: unknown,
): DashboardWidgetPreference[] {
  const allowed = new Set<DashboardWidgetKey>(catalog);
  const defaultSet = new Set<DashboardWidgetKey>(defaults.filter(key => allowed.has(key)));
  const savedOrder: DashboardWidgetKey[] = [];
  const savedMap = new Map<DashboardWidgetKey, boolean>();
  for (const [key, enabled] of preferenceEntries(saved)) {
    const widgetKey = key as DashboardWidgetKey;
    if (!allowed.has(widgetKey)) continue;
    if (!savedMap.has(widgetKey)) savedOrder.push(widgetKey);
    savedMap.set(widgetKey, enabled);
  }
  for (const key of catalog) {
    if (!savedMap.has(key)) savedOrder.push(key);
  }
  return savedOrder.map(key => ({ key, enabled: savedMap.get(key) ?? defaultSet.has(key) }));
}

function savedPreferenceState(saved: unknown): { layoutVersion: number | null; roles: unknown[] | null; widgets: unknown } {
  if (typeof saved === 'string') {
    try { return savedPreferenceState(JSON.parse(saved)); } catch { return { layoutVersion: null, roles: null, widgets: [] }; }
  }
  if (saved && typeof saved === 'object' && !Array.isArray(saved)) {
    const state = saved as { layoutVersion?: unknown; roles?: unknown; widgets?: unknown };
    if (Object.prototype.hasOwnProperty.call(state, 'widgets')) {
      return {
        layoutVersion: typeof state.layoutVersion === 'number' ? state.layoutVersion : null,
        roles: Array.isArray(state.roles) ? state.roles : null,
        widgets: state.widgets,
      };
    }
  }
  return { layoutVersion: null, roles: null, widgets: saved };
}

export function reconcileDashboardPreferences(
  userType: 'EMPLOYEE' | 'CLIENT',
  rolesOrRole: unknown,
  legacyRole: unknown,
  saved: unknown,
): DashboardWidgetPreference[] {
  const currentCatalog = dashboardWidgetCatalog(userType, rolesOrRole, legacyRole);
  const defaults = dashboardDefaults(userType, rolesOrRole, legacyRole);
  const state = savedPreferenceState(saved);
  const defaultSet = new Set(defaults);
  if (state.layoutVersion === DASHBOARD_LAYOUT_VERSION) {
    return normalizeDashboardPreferences(currentCatalog, defaults, state.widgets);
  }

  if (state.layoutVersion === 3) {
    const migrated = normalizeDashboardPreferences(currentCatalog, defaults, state.widgets);
    const serverIndex = migrated.findIndex(item => item.key === 'server');
    const portalIndex = migrated.findIndex(item => item.key === 'system_health');
    if (serverIndex >= 0 && portalIndex >= 0 && serverIndex !== portalIndex + 1) {
      const [server] = migrated.splice(serverIndex, 1);
      migrated.splice(migrated.findIndex(item => item.key === 'system_health') + 1, 0, server);
    }
    return migrated;
  }

  if (state.layoutVersion === 2) {
    const savedMap = new Map<DashboardWidgetKey, boolean>();
    const ordered: DashboardWidgetKey[] = [];
    for (const [key, enabled] of preferenceEntries(state.widgets)) {
      const widgetKey = key as DashboardWidgetKey;
      if (!currentCatalog.includes(widgetKey)) continue;
      if (!savedMap.has(widgetKey)) ordered.push(widgetKey);
      savedMap.set(widgetKey, enabled);
    }
    const previousCatalog = state.roles
      ? new Set(dashboardWidgetCatalog(userType, state.roles, state.roles[0]))
      : new Set(currentCatalog);
    for (const key of currentCatalog) {
      if (ordered.includes(key) || !previousCatalog.has(key)) continue;
      const catalogIndex = currentCatalog.indexOf(key);
      const nextKey = currentCatalog.slice(catalogIndex + 1).find(candidate => ordered.includes(candidate));
      if (nextKey) ordered.splice(ordered.indexOf(nextKey), 0, key);
      else ordered.push(key);
    }
    for (const key of currentCatalog) {
      if (!ordered.includes(key)) ordered.push(key);
    }
    return ordered.map(key => ({ key, enabled: savedMap.get(key) ?? defaultSet.has(key) }));
  }

  const previousCatalog = state.roles
    ? new Set(dashboardWidgetCatalog(userType, state.roles, state.roles[0]))
    : new Set(currentCatalog);
  const savedMap = new Map<DashboardWidgetKey, boolean>();
  for (const [key, enabled] of preferenceEntries(state.widgets)) {
    if (currentCatalog.includes(key as DashboardWidgetKey)) savedMap.set(key as DashboardWidgetKey, enabled);
  }
  return currentCatalog.map(key => ({
    key,
    enabled: previousCatalog.has(key) ? (savedMap.get(key) ?? defaultSet.has(key)) : defaultSet.has(key),
  }));
}

export function moveDashboardWidget(
  preferences: readonly DashboardWidgetPreference[],
  key: DashboardWidgetKey,
  targetIndex: number,
): DashboardWidgetPreference[] {
  const sourceIndex = preferences.findIndex(item => item.key === key);
  if (sourceIndex < 0 || !preferences.length) return preferences as DashboardWidgetPreference[];
  const next = preferences.slice();
  const [moved] = next.splice(sourceIndex, 1);
  const clamped = Math.max(0, Math.min(targetIndex, next.length));
  next.splice(clamped, 0, moved);
  return next;
}
